// src/Services/ReportService.js
import { ServiceProvider } from "./ServiceProvider";
import apiCall from "../utils/apiInterceptor";

class ReportService {
    static async getAll(page_no = 1, count = 10, search = "") {
        try {
            let url = "";

            // ✅ Use filter API if search has value
            if (search && search.trim() !== "") {
                url = `${ServiceProvider.report.filter}?search=${encodeURIComponent(search)}&page_no=${page_no}&count=${count}`;
            }
            // ✅ Otherwise, use normal getAll API
            else {
                url = `${ServiceProvider.report.getAll}?page_no=${page_no}&count=${count}`;
            }

            const response = await apiCall(url, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch reports");
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching reports:", error);
            throw new Error(error.message || "Unable to process your request");
        }
    }

    static async getByStatus(
        status,
        page = 1,
        count = 25,
        search = '',
        startDate = '',
        endDate = '',
        statuses = []
    ) {
        try {
            // Map our internal tab name to the API's expected tab value
            const apiTab = status === 'completed' ? 'complete' : 'pending';

            // Build query params
            const params = new URLSearchParams();

            // Pagination — omit entirely when null (full export)
            if (page != null) params.set('page_no', String(page));
            if (count != null) params.set('count', String(count));

            // Always-present params
            params.set('tab', apiTab);

            if (search) params.set('search_text', search);

            // Date range
            if (startDate) params.set('start_date', startDate);
            if (endDate) params.set('end_date', endDate);

            // Multi-status filter (comma-separated)
            if (statuses && statuses.length > 0) {
                params.set('status', statuses.join(','));
            }

            const url = `${ServiceProvider.report.getByStatus}${params.toString()}`;

            const response = await apiCall(url, { method: 'GET' });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.Error || errorData.message || 'Failed to fetch reports');
            }

            const data = await response.json();

            if (data.success) {
                return {
                    success: true,
                    // Use the tab name as the key (pending / completed)
                    data: data[status] || data[apiTab] || [],
                    count: data[`${status}_count`] || data[`${apiTab}_count`] || 0,
                };
            }

            return { success: false, data: [], count: 0 };

        } catch (error) {
            console.error('ReportService.getByStatus error:', error);
            throw error;
        }
    }

    static async create(dispatchData) {
        try {
            const response = await apiCall(ServiceProvider.report.create, {
                method: "POST",
                body: JSON.stringify(dispatchData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to create dispatch",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error creating dispatch:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }

    static async update(dispatchId, updateData) {
        try {
            const response = await apiCall(`${ServiceProvider.report.update}${dispatchId}`, {
                method: "POST",
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to update report",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error updating report:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }
   
    static async drComplete( updateData) {
        try {
            const response = await apiCall(`${ServiceProvider.report.drComplete}`, {
                method: "POST",
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to update report",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error updating report:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }

    static async delete(dispatchId) {
        try {
            const response = await apiCall(`${ServiceProvider.report.delete}${dispatchId}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to delete dispatch",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error deleting dispatch:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }

    static async getById(dispatchId) {
        try {
            const response = await apiCall(`${ServiceProvider.report.getById}${dispatchId}`, {
                method: "GET",
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw {
                    message: errorData?.message || "Failed to fetch dispatch details",
                    status: response.status,
                };
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching dispatch details:", error);
            if (error.message && error.status) {
                throw error;
            }
            throw {
                message: error.message || "Unable to process your request",
                status: 500
            };
        }
    }
}

export default ReportService;